import React, {useEffect, useState, useCallback} from "react";
import SectionHeading from "./SectionHeading";
import {AiOutlineLoading3Quarters} from "react-icons/ai";
import {
  flattenNeuronRecommendationItems,
  getNeuronRecommendations,
} from "../utils/getNeuronRecommendations";
import {RECOMMENDATION_CONTEXTS} from "../config/recommendationContexts";
import {useDebugMode} from "../hooks/useDebugMode";

const toneBadge = {
  contrarian: {label: "Contrarian", className: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200"},
  supportive: {label: "Supportive", className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200"},
  neutral: {label: "Neutral", className: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"},
};

const mapCommentItems = (items, articleId) => items
  .filter((item) => (
    item?.metadata?.type === "comment"
    && item?.metadata?.parentArticleId === articleId
  ))
  .map((item) => ({
    id: item.id,
    body: item.metadata?.body || item.description || "",
    author: item.metadata?.author || "Anonymous",
    tone: item.metadata?.tone || "neutral",
    likes: Number(item.metadata?.likes ?? 0),
  }));

const CommentsSection = ({articleId, userEmail}) => {
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const debugMode = useDebugMode();

  const fetchComments = useCallback(async () => {
    try {
      const response = await getNeuronRecommendations({
        userId: userEmail,
        contextId: RECOMMENDATION_CONTEXTS.comments,
        scope: {
          filters: [
            {column: "type", operator: "=", value: "comment"},
            {column: "parentArticleId", operator: "=", value: articleId},
          ],
        },
        limit: 100,
      });

      const items = flattenNeuronRecommendationItems(response);
      const mapped = mapCommentItems(items, articleId);

      setComments(mapped);
    } catch (error) {
      console.error("Error fetching comments:", error);
      setComments([]);
    } finally {
      setIsLoading(false);
    }
  }, [articleId, userEmail]);

  useEffect(() => {
    if (!articleId || !userEmail) {
      setComments([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    fetchComments();
  }, [articleId, userEmail, fetchComments]);

  return (
    <section className="mt-12">
      <SectionHeading
        kicker="The thread"
        title="Ranked for you"
        standfirst="Ranked for you, not by time. A rule keeps a dissenting view in slot three."
        debugNote="ctx 2325 · comments ranked per reader"
        debugMode={debugMode}
      />

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-10">
          <AiOutlineLoading3Quarters className="mb-2 animate-spin text-2xl text-ink-muted" />
          <p className="text-body text-ink-tertiary">Setting the letters page...</p>
        </div>
      ) : comments.length > 0 ? (
        <ul className="space-y-4 border-t border-rule pt-4">
          {comments.map((c) => {
            const badge = toneBadge[c.tone] || toneBadge.neutral;
            return (
              <li
                key={c.id}
                className="border-b border-rule pb-4"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{c.author}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${badge.className}`}>
                    {badge.label}
                  </span>
                </div>
                <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                  {c.body}
                </p>
                <p className="text-xs text-gray-500 mt-2">{c.likes} likes</p>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-gray-500 py-6">No comments yet.</p>
      )}
    </section>
  );
};

export default CommentsSection;
